import { fetchSeriesArrayBuffer } from "@api/files";
import { downloadQueue } from "utils/DownloadQueue";
import { inflateSync, unzlibSync } from "fflate";
import {
  estimateDecodedImageBytes,
  isServerCprBaseImagePath,
  isServerFrontDataCprBaseImagePath,
  isServerFrontDataLabelOverlayPath,
  isServerLabelOverlayPath,
  isServerVolumeLabelPath,
  isServerVolumeSlicePath,
  SERVER_CPR_BASE_IMAGE_PREFIX,
  SERVER_FRONT_DATA_CPR_BASE_PREFIX,
  SERVER_FRONT_DATA_LABEL_PREFIX,
  SERVER_LABEL_OVERLAY_PREFIX,
  SERVER_TILE_IMAGE_PREFIX,
  SERVER_VOLUME_LABEL_PREFIX,
  SERVER_VOLUME_SLICE_PREFIX,
  type ImageLayerKey,
  type ImageTileRect,
  type ResolvedImageLayerPaths,
} from "./cprImageResources";
import { createMockImageSvg } from "./mockImageDataProvider";
import {
  createServerLabelOverlayImageBlob,
  createServerLabelOverlayRgbaImage,
  createLocalOverlayImageBlob,
  isLocalOverlayImagePath,
  blobFromRgba,
  type OverlayPaintPolicy,
  type RgbaImageData,
} from "./localOverlayImageProvider";
import {
  uint8ValueToWindowedByte,
  type ImageHuRange,
  type ImageWindowingPolicy,
} from "./imageWindowing";
import {
  fetchServerCprFrontDataTargetBytesCached,
  isServerCprFrontDataRuntimeCacheCurrent,
} from "./frontDataCprDebugLoader";

export type ImageCacheScope = {
  seriesId?: string;
  revision?: string;
  branchId?: string | number | null;
  view?: "cpr" | "cross-section" | string;
  mode?: string | null;
  resourceMode?: string | null;
};

type ResolveImageOptions = {
  seriesId?: string;
  accessToken?: string;
  revision?: string;
  width: number;
  height: number;
  priority?: number;
  maxConcurrent?: number;
  visibility?: Partial<Record<ImageLayerKey, boolean>>;
  localOverlayContextVersion?: number;
  overlayPolicy?: OverlayPaintPolicy;
  windowingPolicy?: ImageWindowingPolicy;
  cacheScope?: ImageCacheScope;
  isPrefetch?: boolean;
  replaceQueuedForScope?: boolean;
  shouldCancel?: () => boolean;
};

type ImageTransformDescriptor = ImageHuRange & {
  tileRect?: ImageTileRect;
  width?: number;
  height?: number;
  outputWidth?: number;
  outputHeight?: number;
  flipY?: boolean;
  labelValues?: number[];
  dtype?: string;
  frameIndex?: number;
  frameStrideBytes?: number;
  uncompressedBytes?: number;
  compression?: string;
};

type CacheEntry = {
  objectUrl: string;
  bytes: number;
  lastUsed: number;
  revision?: string;
  scopeKey?: string;
  kind?: "object-url" | "memory-image";
};

type MemoryImageEntry = RgbaImageData & {
  lastUsed: number;
  revision?: string;
  scopeKey?: string;
};

type PrefetchQueueItem = {
  key: string;
  scopeKey?: string;
  task: () => Promise<void>;
};

type ServerImageCacheCounters = {
  hits: number;
  misses: number;
  pendingHits: number;
  created: number;
  revoked: number;
  revokedByBudget: number;
  revokedByRevision: number;
  revokedByScopeRelease: number;
  sourceHits: number;
  sourceMisses: number;
  volumeHits: number;
  volumeMisses: number;
  prefetchQueued: number;
  prefetchDeduped: number;
  prefetchSkippedStaleScope: number;
};

const MAX_DECODED_CACHE_BYTES = 768 * 1024 * 1024;
const textDecoder = new TextDecoder("utf-8");
const MAX_SOURCE_CACHE_BYTES = 384 * 1024 * 1024;
const MAX_VOLUME_CACHE_BYTES = 512 * 1024 * 1024;
const SERVER_MEMORY_IMAGE_PREFIX = "server-memory-image://";
const cache = new Map<string, CacheEntry>();
const memoryImages = new Map<string, MemoryImageEntry>();
let memoryImageId = 0;
const pending = new Map<string, Promise<string>>();
const sourceCache = new Map<
  string,
  { data: ArrayBuffer; bytes: number; lastUsed: number; revision?: string }
>();
const sourcePending = new Map<string, Promise<ArrayBuffer>>();
const volumeCache = new Map<
  string,
  { data: Uint8Array; bytes: number; lastUsed: number; revision?: string }
>();
const volumePending = new Map<string, Promise<Uint8Array>>();
const frontDataZipDirectoryCache = new Map<
  string,
  { entries: ZipDirectoryEntry[]; lastUsed: number; revision?: string }
>();
const frontDataZipDirectoryPending = new Map<string, Promise<ZipDirectoryEntry[]>>();
const prefetchQueues = new Map<
  number,
  {
    active: number;
    items: PrefetchQueueItem[];
  }
>();
const retainedScopeCounts = new Map<string, number>();
const prefetchQueuedKeys = new Set<string>();
const counters: ServerImageCacheCounters = {
  hits: 0,
  misses: 0,
  pendingHits: 0,
  created: 0,
  revoked: 0,
  revokedByBudget: 0,
  revokedByRevision: 0,
  revokedByScopeRelease: 0,
  sourceHits: 0,
  sourceMisses: 0,
  volumeHits: 0,
  volumeMisses: 0,
  prefetchQueued: 0,
  prefetchDeduped: 0,
  prefetchSkippedStaleScope: 0,
};

const SERVER_CPR_PERF_LOG_ENABLED = false;
const SERVER_CPR_PERF_MIN_MS = 0.25;

function perfNow() {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

function roundMs(value: number) {
  return Math.round(value * 100) / 100;
}

function isFrontDataPerfTarget(value: string | undefined) {
  if (!value) return false;
  return value.includes("front_data") || value.includes("cpr_");
}
function isFrontDataCacheKey(value: string | undefined) {
  return (
    !!value &&
    (value.includes(SERVER_FRONT_DATA_CPR_BASE_PREFIX) ||
      value.includes(SERVER_FRONT_DATA_LABEL_PREFIX) ||
      value.includes("front_data/") ||
      value.includes("front_data%2F") ||
      value.startsWith("front-data-entry|") ||
      value.startsWith("front-data-zip-dir|"))
  );
}

function frontDataSeriesIdFromCacheKey(key: string) {
  const parts = key.split("|");
  if (parts[0] === "front-data-entry" || parts[0] === "front-data-zip-dir") {
    return parts[1];
  }
  return parts[0];
}

function frontDataCacheKeyBelongsToDifferentSeries(
  key: string,
  keepSeriesId?: string,
) {
  const seriesId = frontDataSeriesIdFromCacheKey(key);
  return !keepSeriesId || seriesId !== keepSeriesId;
}

function frontDataScopeBelongsToDifferentSeries(
  scopeKey: string | undefined,
  keepSeriesId?: string,
) {
  if (!keepSeriesId) return true;
  return !scopeKey?.includes(`"seriesId":"${keepSeriesId}"`);
}


function logServerCprPerf(
  _event: string,
  _payload: Record<string, unknown>,
  _elapsedMs?: number,
) {
  // Performance logging disabled for final marker sync cleanup.
}

function throwIfResolveCancelled(options: ResolveImageOptions) {
  if (options.shouldCancel?.()) {
    throw new Error("server image resolve cancelled");
  }
}

function isDirectBrowserImagePath(path: string) {
  return (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("data:") ||
    path.startsWith("blob:")
  );
}

function isMockImagePath(path: string) {
  return path.startsWith("mock://");
}

function parseServerLabelOverlayPath(path: string): {
  layer: ImageLayerKey;
  target: string;
  transform: ImageTransformDescriptor;
} | null {
  if (!isServerLabelOverlayPath(path)) return null;
  const [clean, query] = path.split("?");
  const payload = clean.slice(SERVER_LABEL_OVERLAY_PREFIX.length);
  const slash = payload.indexOf("/");
  if (slash <= 0) return null;
  const layer = payload.slice(0, slash) as ImageLayerKey;
  const encodedTarget = payload.slice(slash + 1);
  if (!encodedTarget) return null;
  const target = decodeURIComponent(encodedTarget);
  return {
    layer,
    target,
    transform: parseImageTransformQuery(query),
  };
}

function parseServerTileImagePath(path: string): {
  target: string;
  transform: ImageTransformDescriptor;
} | null {
  if (!path.startsWith(SERVER_TILE_IMAGE_PREFIX)) return null;
  const [clean, query] = path.split("?");
  const payload = clean.slice(SERVER_TILE_IMAGE_PREFIX.length);
  if (!payload) return null;
  return {
    target: decodeURIComponent(payload),
    transform: parseImageTransformQuery(query),
  };
}

function parseServerCprBaseImagePath(path: string): {
  target: string;
  transform: ImageTransformDescriptor;
} | null {
  if (!isServerCprBaseImagePath(path)) return null;
  const [clean, query] = path.split("?");
  const payload = clean.slice(SERVER_CPR_BASE_IMAGE_PREFIX.length);
  if (!payload) return null;
  return {
    target: decodeURIComponent(payload),
    transform: parseImageTransformQuery(query),
  };
}

type FrontDataZipPathDescriptor = {
  zipTarget: string;
  entryPath: string;
  transform: ImageTransformDescriptor;
};

function parseFrontDataZipEntryPayload(payload: string): {
  zipTarget: string;
  entryPath: string;
} | null {
  const slash = payload.indexOf("/");
  if (slash <= 0 || slash >= payload.length - 1) return null;
  return {
    zipTarget: decodeURIComponent(payload.slice(0, slash)),
    entryPath: decodeURIComponent(payload.slice(slash + 1)),
  };
}

function parseServerFrontDataCprBaseImagePath(
  path: string,
): FrontDataZipPathDescriptor | null {
  if (!isServerFrontDataCprBaseImagePath(path)) return null;
  const [clean, query] = path.split("?");
  const payload = clean.slice(SERVER_FRONT_DATA_CPR_BASE_PREFIX.length);
  const parsed = parseFrontDataZipEntryPayload(payload);
  if (!parsed) return null;
  return {
    ...parsed,
    transform: parseImageTransformQuery(query),
  };
}

function parseServerFrontDataLabelOverlayPath(path: string):
  | (FrontDataZipPathDescriptor & {
      layer: ImageLayerKey;
    })
  | null {
  if (!isServerFrontDataLabelOverlayPath(path)) return null;
  const [clean, query] = path.split("?");
  const payload = clean.slice(SERVER_FRONT_DATA_LABEL_PREFIX.length);
  const slash = payload.indexOf("/");
  if (slash <= 0) return null;
  const layer = payload.slice(0, slash) as ImageLayerKey;
  const parsed = parseFrontDataZipEntryPayload(payload.slice(slash + 1));
  if (!parsed) return null;
  return {
    layer,
    ...parsed,
    transform: parseImageTransformQuery(query),
  };
}

type ZipDirectoryEntry = {
  name: string;
  compressionMethod: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
};

function readZipUint16(view: DataView, offset: number) {
  return view.getUint16(offset, true);
}

function readZipUint32(view: DataView, offset: number) {
  return view.getUint32(offset, true);
}

function findZipEndOfCentralDirectory(view: DataView) {
  const minOffset = Math.max(0, view.byteLength - 0xffff - 22);
  for (let offset = view.byteLength - 22; offset >= minOffset; offset -= 1) {
    if (readZipUint32(view, offset) === 0x06054b50) return offset;
  }
  throw new Error("zip EOCD not found");
}

function listZipEntries(zipBytes: Uint8Array): ZipDirectoryEntry[] {
  const view = new DataView(
    zipBytes.buffer,
    zipBytes.byteOffset,
    zipBytes.byteLength,
  );
  const eocdOffset = findZipEndOfCentralDirectory(view);
  const entryCount = readZipUint16(view, eocdOffset + 10);
  const centralDirectoryOffset = readZipUint32(view, eocdOffset + 16);
  const entries: ZipDirectoryEntry[] = [];
  let offset = centralDirectoryOffset;

  for (let i = 0; i < entryCount; i += 1) {
    if (readZipUint32(view, offset) !== 0x02014b50) {
      throw new Error(`zip central directory is invalid at ${offset}`);
    }
    const compressionMethod = readZipUint16(view, offset + 10);
    const compressedSize = readZipUint32(view, offset + 20);
    const uncompressedSize = readZipUint32(view, offset + 24);
    const nameLength = readZipUint16(view, offset + 28);
    const extraLength = readZipUint16(view, offset + 30);
    const commentLength = readZipUint16(view, offset + 32);
    const localHeaderOffset = readZipUint32(view, offset + 42);
    const nameBytes = new Uint8Array(
      zipBytes.buffer,
      zipBytes.byteOffset + offset + 46,
      nameLength,
    );
    const name = textDecoder.decode(nameBytes);
    entries.push({
      name,
      compressionMethod,
      compressedSize,
      uncompressedSize,
      localHeaderOffset,
    });
    offset += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

function extractZipEntry(zipBytes: Uint8Array, entry: ZipDirectoryEntry) {
  const view = new DataView(
    zipBytes.buffer,
    zipBytes.byteOffset,
    zipBytes.byteLength,
  );
  const offset = entry.localHeaderOffset;
  if (readZipUint32(view, offset) !== 0x04034b50) {
    throw new Error(`zip local header is invalid: ${entry.name}`);
  }
  const nameLength = readZipUint16(view, offset + 26);
  const extraLength = readZipUint16(view, offset + 28);
  const dataOffset = offset + 30 + nameLength + extraLength;
  const compressed = zipBytes.subarray(
    dataOffset,
    dataOffset + entry.compressedSize,
  );

  if (entry.compressionMethod === 0) return compressed;
  if (entry.compressionMethod === 8) return inflateSync(compressed);
  throw new Error(
    `unsupported zip compression method ${entry.compressionMethod}: ${entry.name}`,
  );
}

function normalizeZipEntryPath(path: string) {
  return path.replace(/^\.\//, "").replace(/^\/+/, "");
}

function findZipEntry(entries: ZipDirectoryEntry[], path: string) {
  const cleanPath = normalizeZipEntryPath(path);
  return (
    entries.find((entry) => entry.name === cleanPath) ??
    entries.find((entry) => entry.name.endsWith(`/${cleanPath}`))
  );
}

function frontDataZipDirectoryKey({
  seriesId,
  revision,
  zipTarget,
}: {
  seriesId: string;
  revision?: string;
  zipTarget: string;
}) {
  return [
    'front-data-zip-dir',
    seriesId,
    revision ?? 'no-revision',
    normalizeSeriesImageTarget(zipTarget),
  ].join('|');
}

async function getFrontDataZipDirectoryCached({
  accessToken,
  seriesId,
  zipTarget,
  revision,
}: {
  accessToken: string;
  seriesId: string;
  zipTarget: string;
  revision?: string;
}) {
  const key = frontDataZipDirectoryKey({ seriesId, revision, zipTarget });
  const cached = frontDataZipDirectoryCache.get(key);
  if (cached) {
    cached.lastUsed = Date.now();
    logServerCprPerf("zip-directory-cache-hit", { zipTarget, entryCount: cached.entries.length });
    return cached.entries;
  }

  const existing = frontDataZipDirectoryPending.get(key);
  if (existing) {
    logServerCprPerf("zip-directory-pending-hit", { zipTarget });
    return existing;
  }

  const promise = (async () => {
    const totalStart = perfNow();
    const fetchStart = perfNow();
    const zipBytes = await fetchServerCprFrontDataTargetBytesCached({
      accessToken,
      seriesId,
      target: zipTarget,
    });
    const fetchMs = perfNow() - fetchStart;
    const listStart = perfNow();
    const entries = listZipEntries(zipBytes);
    const listMs = perfNow() - listStart;
    if (isServerCprFrontDataRuntimeCacheCurrent(seriesId)) {
      frontDataZipDirectoryCache.set(key, {
        entries,
        lastUsed: Date.now(),
        revision,
      });
    }
    if (frontDataZipDirectoryCache.size > 24) {
      const staleKeys = [...frontDataZipDirectoryCache.entries()]
        .sort((a, b) => a[1].lastUsed - b[1].lastUsed)
        .slice(0, Math.max(0, frontDataZipDirectoryCache.size - 24))
        .map(([staleKey]) => staleKey);
      staleKeys.forEach((staleKey) => frontDataZipDirectoryCache.delete(staleKey));
    }
    logServerCprPerf("zip-directory-list", {
      zipTarget,
      entryCount: entries.length,
      zipBytes: zipBytes.byteLength,
      fetchMs: roundMs(fetchMs),
      listMs: roundMs(listMs),
    }, perfNow() - totalStart);
    return entries;
  })();

  frontDataZipDirectoryPending.set(key, promise);
  try {
    return await promise;
  } finally {
    frontDataZipDirectoryPending.delete(key);
  }
}

async function fetchFrontDataZipEntryCached({
  accessToken,
  seriesId,
  zipTarget,
  entryPath,
  revision,
}: {
  accessToken: string;
  seriesId: string;
  zipTarget: string;
  entryPath: string;
  revision?: string;
}) {
  const totalStart = perfNow();
  const sourceStart = perfNow();
  const zipBytes = await fetchServerCprFrontDataTargetBytesCached({
    accessToken,
    seriesId,
    target: zipTarget,
  });
  const sourceMs = perfNow() - sourceStart;
  const directoryStart = perfNow();
  const entries = await getFrontDataZipDirectoryCached({
    accessToken,
    seriesId,
    zipTarget,
    revision,
  });
  const directoryMs = perfNow() - directoryStart;
  const findStart = perfNow();
  const entry = findZipEntry(entries, entryPath);
  const findMs = perfNow() - findStart;
  if (!entry) {
    throw new Error(`zip entry not found: ${zipTarget} :: ${entryPath}`);
  }
  const extractStart = perfNow();
  const extracted = extractZipEntry(zipBytes, entry);
  const extractMs = perfNow() - extractStart;
  logServerCprPerf("zip-entry-extract", {
    zipTarget,
    entryPath,
    zipMethod: entry.compressionMethod,
    compressedBytes: entry.compressedSize,
    extractedBytes: extracted.byteLength,
    sourceMs: roundMs(sourceMs),
    directoryMs: roundMs(directoryMs),
    findMs: roundMs(findMs),
    extractMs: roundMs(extractMs),
  }, perfNow() - totalStart);
  return extracted;
}

async function fetchFrontDataZipEntryDecodedCached({
  accessToken,
  seriesId,
  zipTarget,
  entryPath,
  revision,
  compression,
  uncompressedBytes,
}: {
  accessToken: string;
  seriesId: string;
  zipTarget: string;
  entryPath: string;
  revision?: string;
  compression?: string;
  uncompressedBytes?: number;
}) {
  const key = [
    "front-data-entry",
    seriesId,
    revision ?? "no-revision",
    normalizeSeriesImageTarget(zipTarget),
    normalizeZipEntryPath(entryPath),
    compression ?? "raw",
    uncompressedBytes ?? "unknown-size",
  ].join("|");

  const cached = volumeCache.get(key);
  if (cached) {
    cached.lastUsed = Date.now();
    counters.volumeHits += 1;
    logServerCprPerf("front-data-entry-cache-hit", {
      zipTarget,
      entryPath,
      bytes: cached.data.byteLength,
      compression: compression ?? "raw",
    });
    return cached.data;
  }

  const existing = volumePending.get(key);
  if (existing) {
    logServerCprPerf("front-data-entry-pending-hit", { zipTarget, entryPath });
    return existing;
  }

  counters.volumeMisses += 1;
  const promise = (async () => {
    const totalStart = perfNow();
    const zipEntryStart = perfNow();
    const data = await fetchFrontDataZipEntryCached({
      accessToken,
      seriesId,
      zipTarget,
      entryPath,
      revision,
    });
    const zipEntryMs = perfNow() - zipEntryStart;
    const innerDecodeStart = perfNow();
    const decoded = decodeCompressedBytes({
      data,
      compression,
      uncompressedBytes,
      target: `${zipTarget}::${entryPath}`,
    });
    const innerDecodeMs = perfNow() - innerDecodeStart;
    volumeCache.set(key, {
      data: decoded,
      bytes: decoded.byteLength,
      lastUsed: Date.now(),
      revision,
    });
    evictVolumeCacheToBudget();
    logServerCprPerf("front-data-entry-decode", {
      zipTarget,
      entryPath,
      inputBytes: data.byteLength,
      outputBytes: decoded.byteLength,
      compression: compression ?? "raw",
      zipEntryMs: roundMs(zipEntryMs),
      innerDecodeMs: roundMs(innerDecodeMs),
    }, perfNow() - totalStart);
    return decoded;
  })();

  volumePending.set(key, promise);
  try {
    return await promise;
  } finally {
    volumePending.delete(key);
  }
}

function parseServerVolumeSlicePath(path: string): {
  layer: "base";
  target: string;
  transform: ImageTransformDescriptor;
} | null {
  if (!isServerVolumeSlicePath(path)) return null;
  const [clean, query] = path.split("?");
  const payload = clean.slice(SERVER_VOLUME_SLICE_PREFIX.length);
  const slash = payload.indexOf("/");
  if (slash <= 0) return null;
  const layer = payload.slice(0, slash);
  if (layer !== "base") return null;
  const encodedTarget = payload.slice(slash + 1);
  if (!encodedTarget) return null;
  return {
    layer: "base",
    target: decodeURIComponent(encodedTarget),
    transform: parseImageTransformQuery(query),
  };
}

function parseServerVolumeLabelPath(path: string): {
  layer: ImageLayerKey;
  target: string;
  transform: ImageTransformDescriptor;
} | null {
  if (!isServerVolumeLabelPath(path)) return null;
  const [clean, query] = path.split("?");
  const payload = clean.slice(SERVER_VOLUME_LABEL_PREFIX.length);
  const slash = payload.indexOf("/");
  if (slash <= 0) return null;
  const layer = payload.slice(0, slash) as ImageLayerKey;
  const encodedTarget = payload.slice(slash + 1);
  if (!encodedTarget) return null;
  return {
    layer,
    target: decodeURIComponent(encodedTarget),
    transform: parseImageTransformQuery(query),
  };
}

function numberParam(params: URLSearchParams, key: string) {
  const raw = params.get(key);
  if (raw === null || raw.trim() === "") return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

function numberListParam(params: URLSearchParams, key: string) {
  const raw = params.get(key);
  if (!raw) return undefined;
  const values = raw
    .split(",")
    .map((value) => Number(value))
    .filter(Number.isFinite);
  return values.length ? values : undefined;
}

function parseImageTransformQuery(
  query: string | undefined,
): ImageTransformDescriptor {
  const params = new URLSearchParams(query ?? "");
  const tileX = numberParam(params, "tileX");
  const tileY = numberParam(params, "tileY");
  const tileWidth = numberParam(params, "tileWidth");
  const tileHeight = numberParam(params, "tileHeight");
  return {
    width: numberParam(params, "width"),
    height: numberParam(params, "height"),
    outputWidth: numberParam(params, "outputWidth"),
    outputHeight: numberParam(params, "outputHeight"),
    flipY: params.get("flipY") === "1",
    labelValues: numberListParam(params, "values"),
    dtype: params.get("dtype") ?? undefined,
    huMin: numberParam(params, "huMin"),
    huMax: numberParam(params, "huMax"),
    frameIndex: numberParam(params, "frameIndex"),
    frameStrideBytes: numberParam(params, "frameStrideBytes"),
    uncompressedBytes: numberParam(params, "uncompressedBytes"),
    compression: params.get("compression") ?? undefined,
    tileRect:
      tileX !== undefined &&
      tileY !== undefined &&
      tileWidth !== undefined &&
      tileHeight !== undefined
        ? {
            x: tileX,
            y: tileY,
            width: tileWidth,
            height: tileHeight,
          }
        : undefined,
  };
}

function shouldResolveLayer(
  key: ImageLayerKey,
  visibility?: Partial<Record<ImageLayerKey, boolean>>,
) {
  return key === "base" || visibility?.[key] !== false;
}

function toSafeDimension(value: number) {
  return Math.max(1, Math.floor(Number.isFinite(value) ? value : 1));
}

function createObjectUrl(blob: Blob) {
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
    return "";
  }
  return URL.createObjectURL(blob);
}

async function decodeObjectUrlImageIfPossible(objectUrl: string) {
  if (!objectUrl) return;
  if (typeof Image === "undefined") return;

  const image = new Image();
  try {
    image.decoding = "sync";
  } catch (_err) {
    // Older Electron/Chromium builds may expose decoding as read-only.
  }

  const loadPromise = new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error(`image decode failed: ${objectUrl.slice(0, 32)}`));
  });

  image.src = objectUrl;

  if (typeof image.decode === "function") {
    try {
      await image.decode();
      return;
    } catch (_err) {
      // Some blob/SVG cases fail decode() even though onload succeeds.
    }
  }

  await loadPromise.catch(() => undefined);
}

async function createDecodedObjectUrl(blob: Blob) {
  const objectUrl = createObjectUrl(blob);
  if (!objectUrl) return "";
  await decodeObjectUrlImageIfPossible(objectUrl);
  return objectUrl;
}


function mimeFromPath(path: string) {
  const cleanPath = path.split("?")[0].toLowerCase();
  if (cleanPath.endsWith(".jpg") || cleanPath.endsWith(".jpeg")) {
    return "image/jpeg";
  }
  if (cleanPath.endsWith(".webp")) return "image/webp";
  if (cleanPath.endsWith(".svg")) return "image/svg+xml";
  return "image/png";
}

function isLocalOverlayContextSensitivePath(path: string) {
  return (
    isServerLabelOverlayPath(path) ||
    isServerVolumeLabelPath(path) ||
    isServerFrontDataLabelOverlayPath(path) ||
    isLocalOverlayImagePath(path) ||
    isMockImagePath(path)
  );
}

function stableStringify(value: unknown): string {
  if (value === undefined) return "undefined";
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableStringify(entry)).join(",")}]`;
  }
  const object = value as Record<string, unknown>;
  return `{${Object.keys(object)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(object[key])}`)
    .join(",")}}`;
}

function cacheScopeKey(scope?: ImageCacheScope) {
  if (!scope) return undefined;
  return stableStringify({
    seriesId: scope.seriesId ?? null,
    revision: scope.revision ?? null,
    branchId:
      scope.branchId === null || scope.branchId === undefined
        ? null
        : String(scope.branchId),
    view: scope.view ?? null,
    mode: scope.mode ?? null,
    resourceMode: scope.resourceMode ?? null,
  });
}

function isScopeRetained(scopeKey: string | undefined) {
  return !!scopeKey && retainedScopeCounts.has(scopeKey);
}

function revokeDecodedEntry(
  key: string,
  entry: CacheEntry,
  reason: "budget" | "revision" | "scope-release" | "test-reset",
) {
  if (entry.kind === "memory-image") {
    memoryImages.delete(entry.objectUrl);
  } else {
    URL.revokeObjectURL(entry.objectUrl);
  }
  cache.delete(key);
  counters.revoked += 1;
  if (reason === "budget") counters.revokedByBudget += 1;
  if (reason === "revision") counters.revokedByRevision += 1;
  if (reason === "scope-release") counters.revokedByScopeRelease += 1;
}

function clearQueuedPrefetchForScope(scopeKey: string) {
  prefetchQueues.forEach((state) => {
    const retainedItems: PrefetchQueueItem[] = [];
    state.items.forEach((item) => {
      if (item.scopeKey === scopeKey) {
        prefetchQueuedKeys.delete(item.key);
        return;
      }
      retainedItems.push(item);
    });
    state.items = retainedItems;
  });
}

function clearDecodedEntriesForScope(scopeKey: string) {
  for (const [key, entry] of cache.entries()) {
    if (entry.scopeKey !== scopeKey) continue;
    revokeDecodedEntry(key, entry, "scope-release");
  }
  clearQueuedPrefetchForScope(scopeKey);
}

function overlayPolicyCacheKey(policy?: OverlayPaintPolicy) {
  if (!policy) return null;
  const config = policy.config ?? {};
  return stableStringify({
    analysisType: policy.analysisType ?? null,
    colors: config.colors,
    avc_indexs: config.avc_indexs,
    aorta_colors: config.aorta_colors,
    aorta_indexs: config.aorta_indexs,
    aorta_opacitys: config.aorta_opacitys,
  });
}

function windowingPolicyCacheKey(policy?: ImageWindowingPolicy) {
  if (!policy) return "default-window";
  return stableStringify({
    windowLevel: Number.isFinite(policy.windowLevel)
      ? policy.windowLevel
      : null,
    windowWidth: Number.isFinite(policy.windowWidth)
      ? policy.windowWidth
      : null,
    invert: policy.invert === true,
  });
}

function contextCacheToken(path: string, options: ResolveImageOptions) {
  if (
    isServerLabelOverlayPath(path) ||
    isServerVolumeLabelPath(path) ||
    isServerFrontDataLabelOverlayPath(path)
  ) {
    return (
      overlayPolicyCacheKey(options.overlayPolicy) ??
      options.localOverlayContextVersion ??
      0
    );
  }
  if (
    isServerCprBaseImagePath(path) ||
    isServerVolumeSlicePath(path) ||
    isServerFrontDataCprBaseImagePath(path)
  ) {
    return windowingPolicyCacheKey(options.windowingPolicy);
  }
  return isLocalOverlayContextSensitivePath(path)
    ? (options.localOverlayContextVersion ?? 0)
    : "static-context";
}

function cacheKey(path: string, options: ResolveImageOptions) {
  return [
    options.seriesId ?? "no-series",
    options.revision ?? "no-revision",
    toSafeDimension(options.width),
    toSafeDimension(options.height),
    contextCacheToken(path, options),
    path,
  ].join("|");
}

function currentCacheBytes() {
  let total = 0;
  cache.forEach((entry) => {
    total += entry.bytes;
  });
  return total;
}

function currentSourceCacheBytes() {
  let total = 0;
  sourceCache.forEach((entry) => {
    total += entry.bytes;
  });
  return total;
}

function currentVolumeCacheBytes() {
  let total = 0;
  volumeCache.forEach((entry) => {
    total += entry.bytes;
  });
  return total;
}

function evictToBudget() {
  let total = currentCacheBytes();
  if (total <= MAX_DECODED_CACHE_BYTES) return;

  const entries = Array.from(cache.entries()).sort((a, b) => {
    const retainedA = isScopeRetained(a[1].scopeKey) ? 1 : 0;
    const retainedB = isScopeRetained(b[1].scopeKey) ? 1 : 0;
    if (retainedA !== retainedB) return retainedA - retainedB;
    return a[1].lastUsed - b[1].lastUsed;
  });
  for (const [key, entry] of entries) {
    revokeDecodedEntry(key, entry, "budget");
    total -= entry.bytes;
    if (total <= MAX_DECODED_CACHE_BYTES) break;
  }
}

function evictSourceCacheToBudget() {
  let total = currentSourceCacheBytes();
  if (total <= MAX_SOURCE_CACHE_BYTES) return;
  const entries = Array.from(sourceCache.entries()).sort(
    (a, b) => a[1].lastUsed - b[1].lastUsed,
  );
  for (const [key, entry] of entries) {
    sourceCache.delete(key);
    total -= entry.bytes;
    if (total <= MAX_SOURCE_CACHE_BYTES) break;
  }
}

function evictVolumeCacheToBudget() {
  let total = currentVolumeCacheBytes();
  if (total <= MAX_VOLUME_CACHE_BYTES) return;
  const entries = Array.from(volumeCache.entries()).sort(
    (a, b) => a[1].lastUsed - b[1].lastUsed,
  );
  for (const [key, entry] of entries) {
    volumeCache.delete(key);
    total -= entry.bytes;
    if (total <= MAX_VOLUME_CACHE_BYTES) break;
  }
}

function storeCacheEntry(
  key: string,
  objectUrl: string,
  options: ResolveImageOptions,
) {
  const bytes = estimateDecodedImageBytes(options.width, options.height, 1, 1);
  const scopeKey = cacheScopeKey(options.cacheScope);
  cache.set(key, {
    objectUrl,
    bytes,
    lastUsed: Date.now(),
    revision: options.revision,
    scopeKey,
    kind: "object-url",
  });
  counters.created += 1;
  if (scopeKey && !isScopeRetained(scopeKey)) {
    clearDecodedEntriesForScope(scopeKey);
    return objectUrl;
  }
  evictToBudget();
  return objectUrl;
}

function storeMemoryImageEntry(
  key: string,
  image: RgbaImageData,
  options: ResolveImageOptions,
) {
  const bytes = image.rgba.byteLength;
  const scopeKey = cacheScopeKey(options.cacheScope);
  const objectUrl = `${SERVER_MEMORY_IMAGE_PREFIX}${++memoryImageId}`;
  memoryImages.set(objectUrl, {
    ...image,
    lastUsed: Date.now(),
    revision: options.revision,
    scopeKey,
  });
  cache.set(key, {
    objectUrl,
    bytes,
    lastUsed: Date.now(),
    revision: options.revision,
    scopeKey,
    kind: "memory-image",
  });
  counters.created += 1;
  if (scopeKey && !isScopeRetained(scopeKey)) {
    clearDecodedEntriesForScope(scopeKey);
    return objectUrl;
  }
  evictToBudget();
  return objectUrl;
}

export function isServerMemoryImageSource(path: string | undefined) {
  return typeof path === "string" && path.startsWith(SERVER_MEMORY_IMAGE_PREFIX);
}

export function getServerMemoryImageData(path: string | undefined) {
  if (!isServerMemoryImageSource(path)) return undefined;
  const image = memoryImages.get(path);
  if (image) image.lastUsed = Date.now();
  return image;
}

function normalizeSeriesImageTarget(path: string) {
  return path
    .split("?")[0]
    .replace(/^\.\//, "")
    .replace(/^\/+/, "")
    .replaceAll("/", "%2F");
}

async function fetchSeriesArrayBufferCached({
  accessToken,
  seriesId,
  target,
  revision,
}: {
  accessToken: string;
  seriesId: string;
  target: string;
  revision?: string;
}) {
  const normalizedTarget = normalizeSeriesImageTarget(target);
  const key = [seriesId, revision ?? "no-revision", normalizedTarget].join("|");
  const cached = sourceCache.get(key);
  if (cached) {
    cached.lastUsed = Date.now();
    counters.sourceHits += 1;
    if (isFrontDataPerfTarget(target)) {
      logServerCprPerf("source-cache-hit", { target, bytes: cached.data.byteLength });
    }
    return cached.data;
  }
  const existing = sourcePending.get(key);
  if (existing) {
    if (isFrontDataPerfTarget(target)) {
      logServerCprPerf("source-pending-hit", { target });
    }
    return existing;
  }
  counters.sourceMisses += 1;
  const promise = (async () => {
    const totalStart = perfNow();
    const response = await fetchSeriesArrayBuffer({
      accessToken,
      seriesId,
      target: normalizedTarget,
    });
    if (response?.status !== 200 || !response.data) {
      throw new Error(`image download failed: ${target}`);
    }
    const data =
      response.data instanceof ArrayBuffer
        ? response.data
        : (response.data as ArrayBuffer);
    sourceCache.set(key, {
      data,
      bytes: data.byteLength,
      lastUsed: Date.now(),
      revision,
    });
    evictSourceCacheToBudget();
    if (isFrontDataPerfTarget(target)) {
      logServerCprPerf("source-download", {
        target,
        normalizedTarget,
        bytes: data.byteLength,
        status: response?.status,
      }, perfNow() - totalStart);
    }
    return data;
  })();
  sourcePending.set(key, promise);
  try {
    return await promise;
  } finally {
    sourcePending.delete(key);
  }
}

async function fetchSeriesVolumeDataCached({
  accessToken,
  seriesId,
  target,
  revision,
  compression,
  uncompressedBytes,
}: {
  accessToken: string;
  seriesId: string;
  target: string;
  revision?: string;
  compression?: string;
  uncompressedBytes?: number;
}) {
  const normalizedTarget = normalizeSeriesImageTarget(target);
  const key = [
    seriesId,
    revision ?? "no-revision",
    normalizedTarget,
    compression ?? "raw",
  ].join("|");
  const cached = volumeCache.get(key);
  if (cached) {
    cached.lastUsed = Date.now();
    counters.volumeHits += 1;
    return cached.data;
  }
  const existing = volumePending.get(key);
  if (existing) return existing;
  counters.volumeMisses += 1;
  const promise = (async () => {
    const data = await fetchSeriesArrayBufferCached({
      accessToken,
      seriesId,
      target,
      revision,
    });
    const compressed = new Uint8Array(data);
    const inflated =
      !compression || compression === "raw" || compression === "none"
        ? compressed
        : compression === "zlib" || compression === "zlib-deflate"
          ? unzlibSync(compressed)
          : (() => {
              throw new Error(`unsupported volume compression: ${compression}`);
            })();
    if (
      uncompressedBytes !== undefined &&
      inflated.byteLength !== uncompressedBytes
    ) {
      throw new Error(
        `volume byte length mismatch: ${target} ${inflated.byteLength} != ${uncompressedBytes}`,
      );
    }
    volumeCache.set(key, {
      data: inflated,
      bytes: inflated.byteLength,
      lastUsed: Date.now(),
      revision,
    });
    evictVolumeCacheToBudget();
    return inflated;
  })();
  volumePending.set(key, promise);
  try {
    return await promise;
  } finally {
    volumePending.delete(key);
  }
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type);
  });
}

async function cropImageBlob(
  sourceBlob: Blob,
  transform: ImageTransformDescriptor,
  mimeType: string,
) {
  const tile = transform.tileRect;
  if (!tile) return sourceBlob;
  if (typeof document === "undefined" || typeof Image === "undefined") {
    return sourceBlob;
  }
  const outputWidth = Math.max(
    1,
    Math.floor(transform.outputWidth ?? tile.width),
  );
  const outputHeight = Math.max(
    1,
    Math.floor(transform.outputHeight ?? tile.height),
  );
  const objectUrl = URL.createObjectURL(sourceBlob);
  try {
    const image = new Image();
    image.decoding = "async";
    image.src = objectUrl;
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("tile source image load failed"));
    });
    const canvas = document.createElement("canvas");
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return sourceBlob;
    ctx.imageSmoothingEnabled = true;
    if ("imageSmoothingQuality" in ctx) {
      ctx.imageSmoothingQuality = "high";
    }
    ctx.drawImage(
      image,
      tile.x,
      tile.y,
      tile.width,
      tile.height,
      0,
      0,
      outputWidth,
      outputHeight,
    );
    return (await canvasToBlob(canvas, mimeType)) ?? sourceBlob;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function sliceVolumeFrame(
  volume: Uint8Array,
  transform: ImageTransformDescriptor,
) {
  const width = toSafeDimension(transform.width ?? transform.outputWidth ?? 1);
  const height = toSafeDimension(
    transform.height ?? transform.outputHeight ?? 1,
  );
  const frameIndex =
    typeof transform.frameIndex === "number" &&
    Number.isFinite(transform.frameIndex)
      ? Math.floor(transform.frameIndex)
      : 0;
  const frameBytes = width * height;
  const frameStrideBytes = Math.max(
    frameBytes,
    Math.floor(transform.frameStrideBytes ?? frameBytes),
  );
  const offset = frameIndex * frameStrideBytes;
  if (offset < 0 || offset + frameBytes > volume.byteLength) {
    throw new Error(
      `volume frame out of range: frame=${frameIndex}, offset=${offset}, bytes=${frameBytes}`,
    );
  }
  return {
    width,
    height,
    data: volume.slice(offset, offset + frameBytes),
  };
}

function decodeCompressedBytes({
  data,
  compression,
  uncompressedBytes,
  target,
}: {
  data: ArrayBuffer | Uint8Array;
  compression?: string;
  uncompressedBytes?: number;
  target: string;
}) {
  const source = data instanceof Uint8Array ? data : new Uint8Array(data);
  const decoded =
    !compression || compression === "raw" || compression === "none"
      ? source
      : compression === "zlib" || compression === "zlib-deflate"
        ? unzlibSync(source)
        : (() => {
            throw new Error(`unsupported label compression: ${compression}`);
          })();

  if (
    uncompressedBytes !== undefined &&
    decoded.byteLength !== uncompressedBytes
  ) {
    throw new Error(
      `label byte length mismatch: ${target} ${decoded.byteLength} != ${uncompressedBytes}`,
    );
  }

  return decoded;
}

const isLittleEndian = (() => {
  const buffer = new ArrayBuffer(4);
  new Uint32Array(buffer)[0] = 0x0a0b0c0d;
  return new Uint8Array(buffer)[0] === 0x0d;
})();

const grayscaleRgbaLookup = (() => {
  const table = new Uint32Array(256);
  for (let value = 0; value < table.length; value += 1) {
    table[value] = 0xff000000 | (value << 16) | (value << 8) | value;
  }
  return table;
})();

function packGrayscaleRgba(value: number) {
  return 0xff000000 | (value << 16) | (value << 8) | value;
}

const signedInt16WindowLookupCache = new Map<string, Uint8Array>();
const unsignedInt16WindowLookupCache = new Map<string, Uint8Array>();

function trimLookupCache(cacheToTrim: Map<string, Uint8Array>, maxEntries = 12) {
  if (cacheToTrim.size <= maxEntries) return;
  const staleKeys = Array.from(cacheToTrim.keys()).slice(0, cacheToTrim.size - maxEntries);
  staleKeys.forEach((key) => cacheToTrim.delete(key));
}

function createSignedInt16WindowLookup(policy?: ImageWindowingPolicy) {
  const key = windowingPolicyCacheKey(policy);
  const cached = signedInt16WindowLookupCache.get(key);
  if (cached) return cached;

  const lookup = new Uint8Array(65536);
  if (!policy) {
    for (let index = 0; index < lookup.length; index += 1) {
      const hu = index - 32768;
      lookup[index] = Math.max(0, Math.min(255, Math.round(hu)));
    }
  } else {
    const windowLevel = Number.isFinite(policy.windowLevel)
      ? policy.windowLevel
      : 127.5;
    const windowWidth = Math.max(1, Number.isFinite(policy.windowWidth) ? policy.windowWidth : 255);
    const lower = windowLevel - 0.5;
    const denominator = windowWidth - 1;
    for (let index = 0; index < lookup.length; index += 1) {
      const hu = index - 32768;
      const normalized =
        windowWidth <= 1
          ? hu >= windowLevel
            ? 1
            : 0
          : (hu - lower) / denominator + 0.5;
      const clamped = normalized <= 0 ? 0 : normalized >= 1 ? 1 : normalized;
      const byte = Math.round(clamped * 255);
      lookup[index] = policy.invert ? 255 - byte : byte;
    }
  }

  signedInt16WindowLookupCache.set(key, lookup);
  trimLookupCache(signedInt16WindowLookupCache);
  return lookup;
}

function createUnsignedInt16WindowLookup(policy?: ImageWindowingPolicy) {
  const key = windowingPolicyCacheKey(policy);
  const cached = unsignedInt16WindowLookupCache.get(key);
  if (cached) return cached;

  const lookup = new Uint8Array(65536);
  if (!policy) {
    for (let index = 0; index < lookup.length; index += 1) {
      lookup[index] = Math.max(0, Math.min(255, Math.round(index)));
    }
  } else {
    const windowLevel = Number.isFinite(policy.windowLevel)
      ? policy.windowLevel
      : 127.5;
    const windowWidth = Math.max(1, Number.isFinite(policy.windowWidth) ? policy.windowWidth : 255);
    const lower = windowLevel - 0.5;
    const denominator = windowWidth - 1;
    for (let index = 0; index < lookup.length; index += 1) {
      const normalized =
        windowWidth <= 1
          ? index >= windowLevel
            ? 1
            : 0
          : (index - lower) / denominator + 0.5;
      const clamped = normalized <= 0 ? 0 : normalized >= 1 ? 1 : normalized;
      const byte = Math.round(clamped * 255);
      lookup[index] = policy.invert ? 255 - byte : byte;
    }
  }

  unsignedInt16WindowLookupCache.set(key, lookup);
  trimLookupCache(unsignedInt16WindowLookupCache);
  return lookup;
}

function createWindowedGrayscaleRgbaLookup({
  transform,
  policy,
}: {
  transform?: ImageHuRange;
  policy?: ImageWindowingPolicy;
}) {
  if (!policy) return grayscaleRgbaLookup;
  const table = new Uint32Array(256);
  for (let value = 0; value < table.length; value += 1) {
    table[value] = packGrayscaleRgba(
      uint8ValueToWindowedByte({
        value,
        huRange: transform,
        policy,
      }),
    );
  }
  return table;
}

function grayscaleSliceToRgba({
  data,
  transform,
  policy,
}: {
  data: Uint8Array;
  transform?: ImageHuRange;
  policy?: ImageWindowingPolicy;
}) {
  const lookup = createWindowedGrayscaleRgbaLookup({
    transform,
    policy,
  });
  const rgba = new Uint8ClampedArray(data.byteLength * 4);
  if (isLittleEndian) {
    const pixels = new Uint32Array(rgba.buffer);
    for (let i = 0; i < data.byteLength; i += 1) {
      pixels[i] = lookup[data[i]];
    }
    return rgba;
  }

  for (let i = 0; i < data.byteLength; i += 1) {
    const offset = i * 4;
    const packed = lookup[data[i]];
    const value = packed & 0xff;
    rgba[offset] = value;
    rgba[offset + 1] = value;
    rgba[offset + 2] = value;
    rgba[offset + 3] = 255;
  }
  return rgba;
}

function normalizeCprBaseDtype(dtype: string | undefined) {
  const clean = (dtype ?? "uint8").trim().toLowerCase();
  if (!clean || clean === "uint8" || clean === "uint8array" || clean === "u8") {
    return "uint8";
  }
  return clean;
}

function bytesPerPixelForCprDtype(dtype: string) {
  if (dtype.includes("int16") || dtype.includes("uint16") || dtype === "i16") {
    return 2;
  }
  if (dtype.includes("float32") || dtype === "f32") return 4;
  return 1;
}

function cropRawCprImageBytes({
  data,
  sourceWidth,
  sourceHeight,
  bytesPerPixel,
  tile,
}: {
  data: Uint8Array;
  sourceWidth: number;
  sourceHeight: number;
  bytesPerPixel: number;
  tile: ImageTileRect;
}) {
  const tileX = Math.max(0, Math.floor(tile.x));
  const tileY = Math.max(0, Math.floor(tile.y));
  const tileWidth = Math.max(1, Math.floor(tile.width));
  const tileHeight = Math.max(1, Math.floor(tile.height));
  if (
    tileX + tileWidth > sourceWidth ||
    tileY + tileHeight > sourceHeight
  ) {
    throw new Error(
      `CPR base tile out of range: tile=${tileX},${tileY},${tileWidth},${tileHeight} source=${sourceWidth}x${sourceHeight}`,
    );
  }

  const rowBytes = tileWidth * bytesPerPixel;
  const sourceRowBytes = sourceWidth * bytesPerPixel;
  const output = new Uint8Array(rowBytes * tileHeight);
  for (let y = 0; y < tileHeight; y += 1) {
    const sourceOffset = (tileY + y) * sourceRowBytes + tileX * bytesPerPixel;
    output.set(
      data.subarray(sourceOffset, sourceOffset + rowBytes),
      y * rowBytes,
    );
  }
  return {
    width: tileWidth,
    height: tileHeight,
    data: output,
  };
}

function validateCprBaseBytes(
  data: Uint8Array,
  transform: ImageTransformDescriptor,
  target: string,
  fallbackWidth: number,
  fallbackHeight: number,
) {
  const dtype = normalizeCprBaseDtype(transform.dtype);
  const bytesPerPixel = bytesPerPixelForCprDtype(dtype);
  const tile = transform.tileRect;

  if (tile) {
    const tileX = Math.max(0, Math.floor(tile.x));
    const tileWidth = Math.max(1, Math.floor(tile.width));
    const sourceWidth = Math.max(1, tileX + tileWidth);
    const sourceRowBytes = sourceWidth * bytesPerPixel;
    const sourceHeight = data.byteLength / sourceRowBytes;
    if (!Number.isInteger(sourceHeight) || sourceHeight <= 0) {
      throw new Error(
        `CPR base byte length mismatch for tiled raw image: ${target} ${data.byteLength} sourceWidth=${sourceWidth} bpp=${bytesPerPixel}`,
      );
    }
    const cropped = cropRawCprImageBytes({
      data,
      sourceWidth,
      sourceHeight,
      bytesPerPixel,
      tile,
    });
    return {
      width: toSafeDimension(transform.outputWidth ?? cropped.width),
      height: toSafeDimension(transform.outputHeight ?? cropped.height),
      data: cropped.data,
      dtype,
    };
  }

  const width = toSafeDimension(
    transform.outputWidth ?? transform.width ?? fallbackWidth,
  );
  const height = toSafeDimension(
    transform.outputHeight ?? transform.height ?? fallbackHeight,
  );
  const expectedBytes = width * height * bytesPerPixel;
  if (data.byteLength !== expectedBytes) {
    throw new Error(
      `CPR base byte length mismatch: ${target} ${data.byteLength} != ${expectedBytes}`,
    );
  }
  return { width, height, data, dtype };
}

function signedInt16ToRgba({
  data,
  policy,
}: {
  data: Uint8Array;
  policy?: ImageWindowingPolicy;
}) {
  const pixelCount = data.byteLength / 2;
  const rgba = new Uint8ClampedArray(pixelCount * 4);
  const lookup = createSignedInt16WindowLookup(policy);

  if (isLittleEndian && data.byteOffset % 2 === 0) {
    const values = new Int16Array(data.buffer, data.byteOffset, pixelCount);
    const pixels = new Uint32Array(rgba.buffer);
    for (let i = 0; i < pixelCount; i += 1) {
      pixels[i] = grayscaleRgbaLookup[lookup[values[i] + 32768]];
    }
    return rgba;
  }

  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  for (let i = 0; i < pixelCount; i += 1) {
    const value = lookup[view.getInt16(i * 2, true) + 32768];
    const offset = i * 4;
    rgba[offset] = value;
    rgba[offset + 1] = value;
    rgba[offset + 2] = value;
    rgba[offset + 3] = 255;
  }
  return rgba;
}

function unsignedInt16ToRgba({
  data,
  policy,
}: {
  data: Uint8Array;
  policy?: ImageWindowingPolicy;
}) {
  const pixelCount = data.byteLength / 2;
  const rgba = new Uint8ClampedArray(pixelCount * 4);
  const lookup = createUnsignedInt16WindowLookup(policy);

  if (isLittleEndian && data.byteOffset % 2 === 0) {
    const values = new Uint16Array(data.buffer, data.byteOffset, pixelCount);
    const pixels = new Uint32Array(rgba.buffer);
    for (let i = 0; i < pixelCount; i += 1) {
      pixels[i] = grayscaleRgbaLookup[lookup[values[i]]];
    }
    return rgba;
  }

  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  for (let i = 0; i < pixelCount; i += 1) {
    const value = lookup[view.getUint16(i * 2, true)];
    const offset = i * 4;
    rgba[offset] = value;
    rgba[offset + 1] = value;
    rgba[offset + 2] = value;
    rgba[offset + 3] = 255;
  }
  return rgba;
}

function cprBaseImageToRgba({
  image,
  transform,
  policy,
}: {
  image: ReturnType<typeof validateCprBaseBytes>;
  transform?: ImageHuRange;
  policy?: ImageWindowingPolicy;
}) {
  if (image.dtype === "uint8") {
    return grayscaleSliceToRgba({
      data: image.data,
      transform,
      policy,
    });
  }
  if (image.dtype.includes("uint16")) {
    return unsignedInt16ToRgba({ data: image.data, policy });
  }
  if (image.dtype.includes("int16") || image.dtype === "i16") {
    return signedInt16ToRgba({ data: image.data, policy });
  }
  throw new Error(`unsupported CPR base dtype: ${image.dtype}`);
}

function enqueueImageResolve(
  task: () => Promise<string>,
  priority: number,
): Promise<string> {
  return new Promise((resolve, reject) => {
    downloadQueue.enqueue(async () => {
      try {
        resolve(await task());
      } catch (err) {
        reject(err);
      }
    }, priority);
  });
}

export function clearServerImageCacheExceptRevision(revision?: string) {
  for (const [key, entry] of cache.entries()) {
    if (entry.revision === revision) continue;
    revokeDecodedEntry(key, entry, "revision");
  }
  for (const [key, entry] of sourceCache.entries()) {
    if (entry.revision === revision) continue;
    sourceCache.delete(key);
  }
  for (const [key, entry] of volumeCache.entries()) {
    if (entry.revision === revision) continue;
    volumeCache.delete(key);
  }
  for (const [key, entry] of frontDataZipDirectoryCache.entries()) {
    if (entry.revision === revision) continue;
    frontDataZipDirectoryCache.delete(key);
  }
}

export function clearServerImageFrontDataCacheExceptSeries(keepSeriesId?: string) {
  for (const [key, entry] of cache.entries()) {
    if (!isFrontDataCacheKey(key)) continue;
    if (!frontDataScopeBelongsToDifferentSeries(entry.scopeKey, keepSeriesId)) {
      continue;
    }
    revokeDecodedEntry(key, entry, "revision");
  }

  for (const key of sourceCache.keys()) {
    if (!isFrontDataCacheKey(key)) continue;
    if (frontDataCacheKeyBelongsToDifferentSeries(key, keepSeriesId)) {
      sourceCache.delete(key);
    }
  }

  for (const key of sourcePending.keys()) {
    if (!isFrontDataCacheKey(key)) continue;
    if (frontDataCacheKeyBelongsToDifferentSeries(key, keepSeriesId)) {
      sourcePending.delete(key);
    }
  }

  for (const key of volumeCache.keys()) {
    if (!key.startsWith("front-data-entry|")) continue;
    if (frontDataCacheKeyBelongsToDifferentSeries(key, keepSeriesId)) {
      volumeCache.delete(key);
    }
  }

  for (const key of volumePending.keys()) {
    if (!key.startsWith("front-data-entry|")) continue;
    if (frontDataCacheKeyBelongsToDifferentSeries(key, keepSeriesId)) {
      volumePending.delete(key);
    }
  }

  for (const key of frontDataZipDirectoryCache.keys()) {
    if (frontDataCacheKeyBelongsToDifferentSeries(key, keepSeriesId)) {
      frontDataZipDirectoryCache.delete(key);
    }
  }

  for (const key of frontDataZipDirectoryPending.keys()) {
    if (frontDataCacheKeyBelongsToDifferentSeries(key, keepSeriesId)) {
      frontDataZipDirectoryPending.delete(key);
    }
  }
}

export function retainServerImageCacheScope(scope?: ImageCacheScope) {
  const key = cacheScopeKey(scope);
  if (!key) return () => undefined;
  retainedScopeCounts.set(key, (retainedScopeCounts.get(key) ?? 0) + 1);
  return () => {
    const nextCount = (retainedScopeCounts.get(key) ?? 0) - 1;
    if (nextCount > 0) {
      retainedScopeCounts.set(key, nextCount);
      return;
    }
    retainedScopeCounts.delete(key);
    clearDecodedEntriesForScope(key);
  };
}

export function getServerImageCacheStats() {
  return {
    decodedBytes: currentCacheBytes(),
    sourceBytes: currentSourceCacheBytes(),
    volumeBytes: currentVolumeCacheBytes(),
    decodedEntryCount: cache.size,
    memoryImageCount: memoryImages.size,
    sourceEntryCount: sourceCache.size,
    volumeEntryCount: volumeCache.size,
    pendingCount: pending.size,
    sourcePendingCount: sourcePending.size,
    volumePendingCount: volumePending.size,
    prefetchQueuedCount: Array.from(prefetchQueues.values()).reduce(
      (total, state) => total + state.items.length,
      0,
    ),
    retainedScopes: Array.from(retainedScopeCounts.keys()),
    counters: { ...counters },
  };
}

export function resetServerImageCacheForTests() {
  for (const [key, entry] of cache.entries()) {
    revokeDecodedEntry(key, entry, "test-reset");
  }
  memoryImages.clear();
  sourceCache.clear();
  sourcePending.clear();
  volumeCache.clear();
  volumePending.clear();
  pending.clear();
  prefetchQueues.clear();
  retainedScopeCounts.clear();
  prefetchQueuedKeys.clear();
  Object.keys(counters).forEach((key) => {
    counters[key as keyof ServerImageCacheCounters] = 0;
  });
}

export async function resolveImageSourceQueued(
  path: string,
  options: ResolveImageOptions,
) {
  if (!path) return path;
  if (isDirectBrowserImagePath(path)) return path;

  const key = cacheKey(path, options);
  const isFrontDataPerfPath =
    isServerFrontDataCprBaseImagePath(path) || isServerFrontDataLabelOverlayPath(path);
  const shouldLogThisResolve = isFrontDataPerfPath && options.isPrefetch !== true;
  const cached = cache.get(key);
  if (cached) {
    const now = Date.now();
    cached.lastUsed = now;
    cached.scopeKey = cacheScopeKey(options.cacheScope) ?? cached.scopeKey;
    if (cached.kind === "memory-image") {
      const memoryImage = memoryImages.get(cached.objectUrl);
      if (memoryImage) memoryImage.lastUsed = now;
    }
    counters.hits += 1;
    if (shouldLogThisResolve) {
      logServerCprPerf("resolve-cache-hit", {
        kind: cached.kind ?? "object-url",
        bytes: cached.bytes,
        path: path.slice(0, 180),
      });
    }
    return cached.objectUrl;
  }

  const existing = pending.get(key);
  if (existing) {
    counters.pendingHits += 1;
    if (shouldLogThisResolve) {
      logServerCprPerf("resolve-pending-hit", { path: path.slice(0, 180) });
    }
    return existing;
  }
  counters.misses += 1;

  const resolveTask = async () => {
    throwIfResolveCancelled(options);
    const localOverlayBlob =
      isLocalOverlayImagePath(path) || isMockImagePath(path)
        ? await createLocalOverlayImageBlob({
            path,
            width: options.width,
            height: options.height,
          })
        : null;
    if (localOverlayBlob) {
      const objectUrl = await createDecodedObjectUrl(localOverlayBlob);
      if (!objectUrl) return path;
      return storeCacheEntry(key, objectUrl, options);
    }

    if (isMockImagePath(path)) {
      const svg = createMockImageSvg({
        path,
        width: options.width,
        height: options.height,
      });
      const objectUrl = await createDecodedObjectUrl(
        new Blob([svg], { type: "image/svg+xml" }),
      );
      if (!objectUrl) return path;
      return storeCacheEntry(key, objectUrl, options);
    }

    if (isLocalOverlayImagePath(path)) {
      throw new Error(`local overlay unavailable: ${path}`);
    }

    if (!options.seriesId || !options.accessToken) {
      const serverCprBase = parseServerCprBaseImagePath(path);
      if (serverCprBase) {
        throw new Error(
          `server CPR base unavailable without credentials: ${serverCprBase.target}`,
        );
      }
      const frontDataCprBase = parseServerFrontDataCprBaseImagePath(path);
      if (frontDataCprBase) {
        throw new Error(
          `front_data CPR base unavailable without credentials: ${frontDataCprBase.zipTarget}`,
        );
      }
      const frontDataLabel = parseServerFrontDataLabelOverlayPath(path);
      if (frontDataLabel) {
        throw new Error(
          `front_data CPR label unavailable without credentials: ${frontDataLabel.zipTarget}`,
        );
      }
      return path;
    }

    const serverCprBase = parseServerCprBaseImagePath(path);
    if (serverCprBase) {
      const data = await fetchSeriesArrayBufferCached({
        accessToken: options.accessToken,
        seriesId: options.seriesId,
        target: serverCprBase.target,
        revision: options.revision,
      });
      const decoded = decodeCompressedBytes({
        data,
        compression: serverCprBase.transform.compression,
        uncompressedBytes: serverCprBase.transform.uncompressedBytes,
        target: serverCprBase.target,
      });
      const image = validateCprBaseBytes(
        decoded,
        serverCprBase.transform,
        serverCprBase.target,
        options.width,
        options.height,
      );
      const blob = await blobFromRgba(
        cprBaseImageToRgba({
          image,
          transform: serverCprBase.transform,
          policy: options.windowingPolicy,
        }),
        image.width,
        image.height,
      );
      if (!blob) {
        throw new Error(`CPR base rasterize failed: ${serverCprBase.target}`);
      }
      const objectUrl = await createDecodedObjectUrl(blob);
      if (!objectUrl) {
        throw new Error(`CPR base object URL failed: ${serverCprBase.target}`);
      }
      return storeCacheEntry(key, objectUrl, {
        ...options,
        width: image.width,
        height: image.height,
      });
    }

    const frontDataCprBase = parseServerFrontDataCprBaseImagePath(path);
    if (frontDataCprBase) {
      const totalStart = perfNow();
      const rawStart = perfNow();
      const decoded = await fetchFrontDataZipEntryDecodedCached({
        accessToken: options.accessToken,
        seriesId: options.seriesId,
        zipTarget: frontDataCprBase.zipTarget,
        entryPath: frontDataCprBase.entryPath,
        revision: options.revision,
        compression: frontDataCprBase.transform.compression,
        uncompressedBytes: frontDataCprBase.transform.uncompressedBytes,
      });
      const rawMs = perfNow() - rawStart;
      throwIfResolveCancelled(options);
      const validateStart = perfNow();
      const image = validateCprBaseBytes(
        decoded,
        frontDataCprBase.transform,
        `${frontDataCprBase.zipTarget}::${frontDataCprBase.entryPath}`,
        options.width,
        options.height,
      );
      const validateMs = perfNow() - validateStart;
      throwIfResolveCancelled(options);
      const rasterStart = perfNow();
      const rgba = cprBaseImageToRgba({
        image,
        transform: frontDataCprBase.transform,
        policy: options.windowingPolicy,
      });
      const rasterMs = perfNow() - rasterStart;
      throwIfResolveCancelled(options);
      const storeStart = perfNow();
      const stored = storeMemoryImageEntry(
        key,
        { rgba, width: image.width, height: image.height },
        {
          ...options,
          width: image.width,
          height: image.height,
        },
      );
      const storeMs = perfNow() - storeStart;
      logServerCprPerf("front-data-base-resolve", {
        zipTarget: frontDataCprBase.zipTarget,
        entryPath: frontDataCprBase.entryPath,
        prefetch: options.isPrefetch === true,
        width: image.width,
        height: image.height,
        rawBytes: decoded.byteLength,
        rgbaBytes: rgba.byteLength,
        rawMs: roundMs(rawMs),
        validateMs: roundMs(validateMs),
        rasterMs: roundMs(rasterMs),
        storeMs: roundMs(storeMs),
        windowing: windowingPolicyCacheKey(options.windowingPolicy),
      }, perfNow() - totalStart);
      return stored;
    }

    const frontDataLabel = parseServerFrontDataLabelOverlayPath(path);
    if (frontDataLabel) {
      const totalStart = perfNow();
      const rawStart = perfNow();
      const labelData = await fetchFrontDataZipEntryDecodedCached({
        accessToken: options.accessToken,
        seriesId: options.seriesId,
        zipTarget: frontDataLabel.zipTarget,
        entryPath: frontDataLabel.entryPath,
        revision: options.revision,
        compression: frontDataLabel.transform.compression,
        uncompressedBytes: frontDataLabel.transform.uncompressedBytes,
      });
      const rawMs = perfNow() - rawStart;
      throwIfResolveCancelled(options);
      const rasterStart = perfNow();
      const image = createServerLabelOverlayRgbaImage({
        seriesId: options.seriesId,
        layer: frontDataLabel.layer,
        labelData,
        width: frontDataLabel.transform.outputWidth ?? options.width,
        height: frontDataLabel.transform.outputHeight ?? options.height,
        transform: frontDataLabel.transform,
        labelValues: frontDataLabel.transform.labelValues,
        overlayPolicy: options.overlayPolicy,
      });
      const rasterMs = perfNow() - rasterStart;
      if (!image) return path;
      throwIfResolveCancelled(options);
      const storeStart = perfNow();
      const stored = storeMemoryImageEntry(key, image, {
        ...options,
        width: image.width,
        height: image.height,
      });
      const storeMs = perfNow() - storeStart;
      logServerCprPerf("front-data-label-resolve", {
        layer: frontDataLabel.layer,
        zipTarget: frontDataLabel.zipTarget,
        entryPath: frontDataLabel.entryPath,
        prefetch: options.isPrefetch === true,
        width: image.width,
        height: image.height,
        rawBytes: labelData.byteLength,
        rgbaBytes: image.rgba.byteLength,
        rawMs: roundMs(rawMs),
        rasterMs: roundMs(rasterMs),
        storeMs: roundMs(storeMs),
      }, perfNow() - totalStart);
      return stored;
    }

    const serverTile = parseServerTileImagePath(path);
    if (serverTile) {
      const data = await fetchSeriesArrayBufferCached({
        accessToken: options.accessToken,
        seriesId: options.seriesId,
        target: serverTile.target,
        revision: options.revision,
      });
      const mimeType = mimeFromPath(serverTile.target);
      const croppedBlob = await cropImageBlob(
        new Blob([data], { type: mimeType }),
        serverTile.transform,
        mimeType,
      );
      const objectUrl = await createDecodedObjectUrl(croppedBlob);
      if (!objectUrl) return path;
      return storeCacheEntry(key, objectUrl, {
        ...options,
        width: serverTile.transform.outputWidth ?? options.width,
        height: serverTile.transform.outputHeight ?? options.height,
      });
    }

    const serverLabel = parseServerLabelOverlayPath(path);
    if (serverLabel) {
      const data = await fetchSeriesArrayBufferCached({
        accessToken: options.accessToken,
        seriesId: options.seriesId,
        target: serverLabel.target,
        revision: options.revision,
      });
      const labelData = decodeCompressedBytes({
        data,
        compression: serverLabel.transform.compression,
        uncompressedBytes: serverLabel.transform.uncompressedBytes,
        target: serverLabel.target,
      });
      const blob = await createServerLabelOverlayImageBlob({
        seriesId: options.seriesId,
        layer: serverLabel.layer,
        labelData,
        width: serverLabel.transform.outputWidth ?? options.width,
        height: serverLabel.transform.outputHeight ?? options.height,
        transform: serverLabel.transform,
        labelValues: serverLabel.transform.labelValues,
        overlayPolicy: options.overlayPolicy,
      });
      if (!blob) return path;
      const objectUrl = await createDecodedObjectUrl(blob);
      if (!objectUrl) return path;
      return storeCacheEntry(key, objectUrl, options);
    }

    const serverVolumeSlice = parseServerVolumeSlicePath(path);
    if (serverVolumeSlice) {
      const volume = await fetchSeriesVolumeDataCached({
        accessToken: options.accessToken,
        seriesId: options.seriesId,
        target: serverVolumeSlice.target,
        revision: options.revision,
        compression: serverVolumeSlice.transform.compression,
        uncompressedBytes: serverVolumeSlice.transform.uncompressedBytes,
      });
      const slice = sliceVolumeFrame(volume, serverVolumeSlice.transform);
      const blob = await blobFromRgba(
        grayscaleSliceToRgba({
          data: slice.data,
          transform: serverVolumeSlice.transform,
          policy: options.windowingPolicy,
        }),
        slice.width,
        slice.height,
      );
      if (!blob) return path;
      const objectUrl = await createDecodedObjectUrl(blob);
      if (!objectUrl) return path;
      return storeCacheEntry(key, objectUrl, {
        ...options,
        width: slice.width,
        height: slice.height,
      });
    }

    const serverVolumeLabel = parseServerVolumeLabelPath(path);
    if (serverVolumeLabel) {
      const volume = await fetchSeriesVolumeDataCached({
        accessToken: options.accessToken,
        seriesId: options.seriesId,
        target: serverVolumeLabel.target,
        revision: options.revision,
        compression: serverVolumeLabel.transform.compression,
        uncompressedBytes: serverVolumeLabel.transform.uncompressedBytes,
      });
      const slice = sliceVolumeFrame(volume, serverVolumeLabel.transform);
      const blob = await createServerLabelOverlayImageBlob({
        seriesId: options.seriesId,
        layer: serverVolumeLabel.layer,
        labelData: slice.data,
        width: slice.width,
        height: slice.height,
        labelValues: serverVolumeLabel.transform.labelValues,
        overlayPolicy: options.overlayPolicy,
      });
      if (!blob) return path;
      const objectUrl = await createDecodedObjectUrl(blob);
      if (!objectUrl) return path;
      return storeCacheEntry(key, objectUrl, {
        ...options,
        width: slice.width,
        height: slice.height,
      });
    }

    const data = await fetchSeriesArrayBufferCached({
      accessToken: options.accessToken,
      seriesId: options.seriesId,
      target: path,
      revision: options.revision,
    });
    const objectUrl = await createDecodedObjectUrl(
      new Blob([data], { type: mimeFromPath(path) }),
    );
    if (!objectUrl) return path;
    return storeCacheEntry(key, objectUrl, options);
  };

  const shouldBypassResolveQueue =
    options.isPrefetch !== true &&
    (isServerFrontDataCprBaseImagePath(path) ||
      isServerFrontDataLabelOverlayPath(path));
  const promise = shouldBypassResolveQueue
    ? resolveTask()
    : enqueueImageResolve(resolveTask, options.priority ?? 100);

  pending.set(key, promise);
  try {
    return await promise;
  } finally {
    pending.delete(key);
  }
}

export async function resolveImageLayerSourcesQueued(
  layers: ResolvedImageLayerPaths | null,
  options: ResolveImageOptions,
): Promise<ResolvedImageLayerPaths | null> {
  if (!layers?.base) return null;
  const base = await resolveImageSourceQueued(layers.base, options);
  const overlayEntries = (
    Object.entries(layers) as Array<[ImageLayerKey, string | undefined]>
  ).filter(
    ([key, path]) =>
      key !== "base" && !!path && shouldResolveLayer(key, options.visibility),
  );
  const settled = await Promise.allSettled(
    overlayEntries.map(async ([key, path]) => {
      const resolved = path
        ? await resolveImageSourceQueued(path, options)
        : undefined;
      return [
        key,
        resolved &&
        (isServerLabelOverlayPath(resolved) ||
          isServerVolumeLabelPath(resolved))
          ? undefined
          : resolved,
      ] as const;
    }),
  );
  const overlays = settled
    .map((result) => (result.status === "fulfilled" ? result.value : null))
    .filter(
      (entry): entry is readonly [ImageLayerKey, string | undefined] => !!entry,
    );
  return {
    base,
    ...Object.fromEntries(overlays),
  } as ResolvedImageLayerPaths;
}

export function prefetchImageSourcesQueued(
  paths: Array<string | undefined>,
  options: ResolveImageOptions,
) {
  const scopeKey = cacheScopeKey(options.cacheScope);
  if (scopeKey && options.replaceQueuedForScope) {
    clearQueuedPrefetchForScope(scopeKey);
  }
  const tasks: PrefetchQueueItem[] = [];
  paths
    .filter(
      (path): path is string => typeof path === "string" && path.length > 0,
    )
    .forEach((path) => {
      if (isDirectBrowserImagePath(path)) return;
      const key = cacheKey(path, options);
      if (cache.has(key) || pending.has(key) || prefetchQueuedKeys.has(key)) {
        counters.prefetchDeduped += 1;
        return;
      }
      prefetchQueuedKeys.add(key);
      counters.prefetchQueued += 1;
      tasks.push({
        key,
        scopeKey,
        task: async () => {
          if (scopeKey && !isScopeRetained(scopeKey)) {
            counters.prefetchSkippedStaleScope += 1;
            return;
          }
          await resolveImageSourceQueued(path, {
            ...options,
            maxConcurrent: undefined,
            priority: options.priority ?? 240,
            isPrefetch: true,
          }).catch(() => undefined);
        },
      });
    });

  const limit =
    Number.isFinite(options.maxConcurrent) && Number(options.maxConcurrent) > 0
      ? Math.floor(Number(options.maxConcurrent))
      : 0;

  if (!limit) {
    tasks.forEach((item) => {
      void item.task().finally(() => {
        prefetchQueuedKeys.delete(item.key);
      });
    });
    return;
  }

  const state = prefetchQueues.get(limit) ?? {
    active: 0,
    items: [],
  };
  prefetchQueues.set(limit, state);

  const pump = () => {
    while (state.active < limit && state.items.length > 0) {
      const item = state.items.shift()!;
      state.active += 1;
      item
        .task()
        .catch(() => undefined)
        .finally(() => {
          prefetchQueuedKeys.delete(item.key);
          state.active -= 1;
          pump();
        });
    }
  };

  state.items.push(...tasks);
  pump();
}
